import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collectionGroup, getDocs, query, orderBy, doc, getDoc, collection, where, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/layout/Header';
import { FileText, Search, Eye, ArrowLeft, Calendar, Building2, User, CreditCard, CheckCircle, XCircle, Filter, Smartphone, Wallet, ExternalLink, Copy, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import { showError, showSuccess, confirmAction } from '@/lib/swal';

interface Payment {
  amount: number;
  date: Date;
  method: string;
  transactionId?: string;
  transactionCode?: string;
  sumupCheckoutId?: string;
  sumupTransactionCode?: string;
  slickpayInvoiceId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  processingFee?: number;
  accountingSplit?: {
    lodgeTransfer: number;
    platformProfit: number;
  };
}

interface Invoice {
  id: string;
  eventId: string;
  unitId: string;
  ownerUid: string;
  totalAmount: number;
  amountPaid: number;
  outstandingBalance: number;
  status: string;
  rsvps: string[];
  paymentHistory: Payment[];
  invoiceNumber?: string;
  subtotal?: number;
  vatAmount?: number;
  vatRate?: number;
  transactionFee?: number;
  accountingTotals?: {
    totalLodgeTransfer: number;
    totalPlatformProfit: number;
  };
  payerEmail?: string;
  payerName?: string;
  isGuestPayment?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface InvoiceWithDetails extends Invoice {
  unitName?: string;
  unitNumber?: string;
  eventName?: string;
  eventDate?: Date;
  ownerName?: string;
  ownerEmail?: string;
  rsvpDetails?: Array<{
    id: string;
    name: string;
    email: string;
    paid: boolean;
    paymentStatus?: string;
    paymentMethod?: string;
  }>;
}

export default function InvoiceManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<InvoiceWithDetails[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<InvoiceWithDetails[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithDetails | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [auditLogs, setAuditLogs] = useState<Array<{
    id: string;
    timestamp: Date;
    action: string;
    userId: string;
    userName: string;
    details: any;
  }>>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [invoiceToVoid, setInvoiceToVoid] = useState<InvoiceWithDetails | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);

  useEffect(() => {
    // Read search query parameter from URL
    try {
      const params = new URLSearchParams(window.location.search);
      const searchParam = params.get('search');
      if (searchParam && searchParam.trim()) {
        setSearchQuery(searchParam.trim());
      }
    } catch (e) {
      // Ignore malformed URL search params
    }
    fetchInvoices();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [searchQuery, statusFilter, paymentMethodFilter, invoices]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching invoices with collectionGroup query...');
      const invoicesQuery = query(
        collectionGroup(db, 'invoices'),
        orderBy('createdAt', 'desc')
      );
      
      console.log('📡 Executing getDocs...');
      const invoicesSnapshot = await getDocs(invoicesQuery);
      console.log('✅ Got invoices snapshot:', invoicesSnapshot.size, 'invoices');
      const invoicesList: InvoiceWithDetails[] = [];

      for (const invoiceDoc of invoicesSnapshot.docs) {
        const data = invoiceDoc.data();
        
        // Extract unitId and eventId from document path
        // Path format: units/{unitId}/events/{eventId}/invoices/{invoiceId}
        const pathParts = invoiceDoc.ref.path.split('/');
        const unitId = pathParts[1];
        const eventId = pathParts[3];

        const invoice: InvoiceWithDetails = {
          id: invoiceDoc.id,
          eventId: eventId || data.eventId || '',
          unitId: unitId || data.unitId || '',
          ownerUid: data.ownerUid || '',
          totalAmount: data.totalAmount || 0,
          amountPaid: data.amountPaid || 0,
          outstandingBalance: data.outstandingBalance || 0,
          status: data.status || 'outstanding',
          rsvps: data.rsvps || [],
          paymentHistory: (data.paymentHistory || []).map((p: any) => ({
            ...p,
            date: p.date?.toDate ? p.date.toDate() : (p.date instanceof Date ? p.date : new Date(p.date)),
          })),
          invoiceNumber: data.invoiceNumber,
          subtotal: data.subtotal,
          vatAmount: data.vatAmount,
          vatRate: data.vatRate,
          transactionFee: data.transactionFee,
          accountingTotals: data.accountingTotals,
          payerEmail: data.payerEmail,
          payerName: data.payerName,
          isGuestPayment: data.isGuestPayment,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt instanceof Date ? data.createdAt : new Date()),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt instanceof Date ? data.updatedAt : new Date()),
        };

        // Fetch unit details
        try {
          const unitDoc = await getDoc(doc(db, 'units', invoice.unitId));
          if (unitDoc.exists()) {
            const unitData = unitDoc.data();
            invoice.unitName = unitData.name || '';
            invoice.unitNumber = unitData.unitNumber || unitData.number || '';
          }
        } catch (error) {
          console.error('Error fetching unit:', error);
        }

        // Fetch event details
        try {
          const eventDoc = await getDoc(doc(db, 'units', invoice.unitId, 'events', invoice.eventId));
          if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            invoice.eventName = eventData.name || eventData.title || '';
            invoice.eventDate = eventData.date?.toDate ? eventData.date.toDate() : (eventData.date instanceof Date ? eventData.date : undefined);
          }
        } catch (error) {
          console.error('Error fetching event:', error);
        }

        // Fetch owner details
        // First check if invoice has payer information (for guest payments)
        if (invoice.payerEmail || invoice.payerName) {
          invoice.ownerEmail = invoice.payerEmail || '';
          invoice.ownerName = invoice.payerName || '';
        } else if (invoice.ownerUid) {
          // Try to fetch from users collection for registered users
          try {
            const userDoc = await getDoc(doc(db, 'users', invoice.ownerUid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              invoice.ownerName = userData.name || '';
              invoice.ownerEmail = userData.email || '';
            }
          } catch (error) {
            console.error('Error fetching user:', error);
          }
        }
        
        // If still no owner information, try to get from RSVPs as a fallback
        if (!invoice.ownerName && !invoice.ownerEmail && invoice.rsvps && invoice.rsvps.length > 0) {
          try {
            const firstRsvpId = invoice.rsvps[0];
            const rsvpDoc = await getDoc(doc(db, 'units', invoice.unitId, 'events', invoice.eventId, 'rsvps', firstRsvpId));
            if (rsvpDoc.exists()) {
              const rsvpData = rsvpDoc.data();
              const rsvpName = rsvpData.name || `${rsvpData.firstName || ''} ${rsvpData.lastName || ''}`.trim() || '';
              const rsvpEmail = rsvpData.email || '';
              
              if (rsvpName || rsvpEmail) {
                invoice.ownerName = invoice.ownerName || rsvpName;
                invoice.ownerEmail = invoice.ownerEmail || rsvpEmail;
              }
            }
          } catch (error) {
            console.error('Error fetching RSVP for owner info:', error);
          }
        }

        invoicesList.push(invoice);
      }

      console.log('📋 Successfully processed', invoicesList.length, 'invoices');
      setInvoices(invoicesList);
    } catch (error: any) {
      console.error('❌ Error fetching invoices:', error);
      console.error('Error code:', error?.code);
      console.error('Error message:', error?.message);
      console.error('Error details:', JSON.stringify(error, null, 2));
      
      // Show more specific error message
      if (error?.code === 'permission-denied') {
        showError('Permission denied: You may not have admin access. Please check your account permissions.');
      } else {
        showError(`Failed to load invoices: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = [...invoices];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Apply payment method filter
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(inv => {
        // Check if invoice has any payments with the selected method
        return inv.paymentHistory.some(payment => payment.method === paymentMethodFilter);
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(inv => {
        // Also search in payment transaction IDs
        const paymentIds = inv.paymentHistory.flatMap(p => [
          p.transactionId,
          p.transactionCode,
          p.sumupCheckoutId,
          p.sumupTransactionCode,
          p.slickpayInvoiceId,
          p.stripePaymentIntentId,
          p.stripeChargeId,
        ].filter(Boolean)).map(id => id?.toLowerCase() || '');
        
        return (
          inv.id.toLowerCase().includes(query) ||
          inv.invoiceNumber?.toLowerCase().includes(query) ||
          inv.unitName?.toLowerCase().includes(query) ||
          inv.unitNumber?.toLowerCase().includes(query) ||
          inv.eventName?.toLowerCase().includes(query) ||
          inv.ownerName?.toLowerCase().includes(query) ||
          inv.ownerEmail?.toLowerCase().includes(query) ||
          paymentIds.some(id => id.includes(query))
        );
      });
    }

    setFilteredInvoices(filtered);
  };

  const handleViewInvoice = async (invoice: InvoiceWithDetails) => {
    setSelectedInvoice(invoice);
    setLoadingDetails(true);
    setLoadingAuditLogs(true);
    setIsDetailDialogOpen(true);

    try {
      // Fetch RSVP details
      const rsvpDetails: Array<{
        id: string;
        name: string;
        email: string;
        paid: boolean;
        paymentStatus?: string;
        paymentMethod?: string;
      }> = [];

      for (const rsvpId of invoice.rsvps) {
        try {
          const rsvpDoc = await getDoc(doc(db, 'units', invoice.unitId, 'events', invoice.eventId, 'rsvps', rsvpId));
          if (rsvpDoc.exists()) {
            const rsvpData = rsvpDoc.data();
            rsvpDetails.push({
              id: rsvpDoc.id,
              name: rsvpData.name || `${rsvpData.firstName || ''} ${rsvpData.lastName || ''}`.trim() || 'Unknown',
              email: rsvpData.email || '',
              paid: rsvpData.paid || false,
              paymentStatus: rsvpData.paymentStatus,
              paymentMethod: rsvpData.paymentMethod,
            });
          }
        } catch (error) {
          console.error('Error fetching RSVP:', error);
        }
      }

      setSelectedInvoice({
        ...invoice,
        rsvpDetails,
      });

      // Fetch audit logs for this invoice
      try {
        const auditLogsQuery = query(
          collection(db, 'units', invoice.unitId, 'auditLog'),
          where('details.invoiceId', '==', invoice.id),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
        
        const auditLogsSnapshot = await getDocs(auditLogsQuery);
        const logs = auditLogsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp instanceof Date ? data.timestamp : new Date()),
            action: data.action || '',
            userId: data.userId || '',
            userName: data.userName || 'System',
            details: data.details || {},
          };
        });
        
        setAuditLogs(logs);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
        // Don't show error - audit logs are optional
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Error loading invoice details:', error);
      showError('Failed to load invoice details');
    } finally {
      setLoadingDetails(false);
      setLoadingAuditLogs(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      paid: { label: 'Paid', variant: 'default' },
      partial: { label: 'Partial', variant: 'secondary' },
      outstanding: { label: 'Outstanding', variant: 'destructive' },
      pending_verification: { label: 'Pending Verification', variant: 'outline' },
      voided: { label: 'Voided', variant: 'outline' },
    };

    const config = statusConfig[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleVoidInvoice = async (invoice: InvoiceWithDetails) => {
    setInvoiceToVoid(invoice);
    setVoidReason('');
    setIsVoidDialogOpen(true);
  };

  const confirmVoidInvoice = async () => {
    if (!invoiceToVoid) return;

    const confirmed = await confirmAction({
      title: 'Void Invoice?',
      text: `Are you sure you want to void Invoice #${invoiceToVoid.invoiceNumber || invoiceToVoid.id.slice(0, 8)}? This action cannot be undone.`,
      confirmButtonText: 'Yes, Void Invoice',
      cancelButtonText: 'Cancel',
    });

    if (!confirmed.isConfirmed) {
      setIsVoidDialogOpen(false);
      setInvoiceToVoid(null);
      setVoidReason('');
      return;
    }

    try {
      setVoiding(true);
      const voidInvoiceFunction = httpsCallable(functions, 'voidInvoice');
      
      await voidInvoiceFunction({
        unitId: invoiceToVoid.unitId,
        eventId: invoiceToVoid.eventId,
        invoiceId: invoiceToVoid.id,
        reason: voidReason.trim() || 'Voided by platform administrator',
      });

      showSuccess('Invoice voided successfully');
      setIsVoidDialogOpen(false);
      setInvoiceToVoid(null);
      setVoidReason('');
      
      // Refresh invoices list
      await fetchInvoices();
    } catch (error: any) {
      console.error('Error voiding invoice:', error);
      showError(error.message || 'Failed to void invoice. Please try again.');
    } finally {
      setVoiding(false);
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    const methodConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline', icon?: React.ReactNode }> = {
      stripe: { label: 'Stripe', variant: 'default', icon: <CreditCard className="h-3 w-3" /> },
      sumup: { label: 'SumUp', variant: 'secondary', icon: <Smartphone className="h-3 w-3" /> },
      slickpay: { label: 'SlickPay', variant: 'secondary', icon: <Wallet className="h-3 w-3" /> },
      bank_transfer: { label: 'Bank Transfer', variant: 'outline', icon: <Building2 className="h-3 w-3" /> },
    };

    const config = methodConfig[method] || { label: method || 'Unknown', variant: 'outline' };
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    // You could use a toast notification here if available
    console.log(`Copied ${label} to clipboard`);
  };

  const getSumUpDashboardUrl = (checkoutId: string) => {
    // SumUp dashboard URL format (this may need to be adjusted based on actual SumUp dashboard structure)
    return `https://me.sumup.com/checkouts/${checkoutId}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading invoices...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 flex flex-col">
        <div className="px-4 py-8">
          <div className="container mx-auto">
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Invoice Management</h1>
                <p className="text-muted-foreground">View and manage all platform invoices</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>

            {/* Search and Filter */}
            <Card className="mb-6">
            <CardHeader>
              <CardTitle>Search & Filter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 md:flex-row">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by invoice ID, number, unit, event, or owner..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="all">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="outstanding">Outstanding</option>
                    <option value="pending_verification">Pending Verification</option>
                    <option value="voided">Voided</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <select
                    value={paymentMethodFilter}
                    onChange={(e) => setPaymentMethodFilter(e.target.value)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="all">All Payment Methods</option>
                    <option value="sumup">SumUp</option>
                    <option value="slickpay">SlickPay</option>
                    <option value="stripe">Stripe</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>

        {/* Full-width invoice table */}
        <div className="flex-1 px-4 pb-8">
          <Card>
            <CardHeader>
              <CardTitle>Invoices ({filteredInvoices.length})</CardTitle>
              <CardDescription>
                {filteredInvoices.length === invoices.length
                  ? `Showing all ${invoices.length} invoices`
                  : `Showing ${filteredInvoices.length} of ${invoices.length} invoices`}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {filteredInvoices.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No invoices found</p>
                </div>
              ) : (
                <div className="rounded-md border-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">Invoice #</TableHead>
                        <TableHead className="w-[100px]">Status</TableHead>
                        <TableHead className="w-[180px]">Unit</TableHead>
                        <TableHead className="w-[200px]">Event</TableHead>
                        <TableHead className="w-[180px]">Owner</TableHead>
                        <TableHead className="w-[100px] text-right">Total</TableHead>
                        <TableHead className="w-[100px] text-right">Paid</TableHead>
                        <TableHead className="w-[100px] text-right">Outstanding</TableHead>
                        <TableHead className="w-[120px]">Payment Methods</TableHead>
                        <TableHead className="w-[130px]">Created</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow key={invoice.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium">
                            {invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : invoice.id.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(invoice.status)}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3 text-muted-foreground" />
                              <span className="truncate max-w-[160px]" title={`${invoice.unitName || 'Unknown Unit'} ${invoice.unitNumber ? `(${invoice.unitNumber})` : ''}`}>
                                {invoice.unitName || 'Unknown Unit'}
                              </span>
                              {invoice.unitNumber && (
                                <span className="text-muted-foreground">({invoice.unitNumber})</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              <span className="truncate max-w-[180px]" title={invoice.eventName || 'Unknown Event'}>
                                {invoice.eventName || 'Unknown Event'}
                              </span>
                            </div>
                            {invoice.eventDate && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {format(invoice.eventDate, 'MMM dd, yyyy')}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {invoice.ownerName || invoice.ownerEmail ? (
                              <div>
                                {invoice.ownerName && (
                                  <div className="flex items-center gap-1">
                                    <User className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium">{invoice.ownerName}</span>
                                  </div>
                                )}
                                {invoice.ownerEmail && (
                                  <div className="text-xs text-muted-foreground truncate max-w-[160px]" title={invoice.ownerEmail}>
                                    {invoice.ownerEmail}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">Not available</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(invoice.totalAmount)}
                          </TableCell>
                          <TableCell className="text-right text-green-600 font-medium">
                            {formatCurrency(invoice.amountPaid)}
                          </TableCell>
                          <TableCell className="text-right">
                            {invoice.outstandingBalance > 0 ? (
                              <span className="text-amber-600 font-medium">
                                {formatCurrency(invoice.outstandingBalance)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {invoice.paymentHistory.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {Array.from(new Set(invoice.paymentHistory.map(p => p.method).filter(Boolean))).map(method => {
                                  if (method === 'stripe') {
                                    return (
                                      <Badge key={method} variant="outline" className="text-xs h-5 px-1.5 flex items-center gap-1">
                                        <CreditCard className="h-2.5 w-2.5" />
                                        Stripe
                                      </Badge>
                                    );
                                  }
                                  if (method === 'sumup') {
                                    return (
                                      <Badge key={method} variant="secondary" className="text-xs h-5 px-1.5 flex items-center gap-1">
                                        <Smartphone className="h-2.5 w-2.5" />
                                        SumUp
                                      </Badge>
                                    );
                                  }
                                  if (method === 'slickpay') {
                                    return (
                                      <Badge key={method} variant="secondary" className="text-xs h-5 px-1.5 flex items-center gap-1">
                                        <Wallet className="h-2.5 w-2.5" />
                                        SlickPay
                                      </Badge>
                                    );
                                  }
                                  if (method === 'bank_transfer') {
                                    return (
                                      <Badge key={method} variant="outline" className="text-xs h-5 px-1.5 flex items-center gap-1">
                                        <Building2 className="h-2.5 w-2.5" />
                                        Bank
                                      </Badge>
                                    );
                                  }
                                  return (
                                    <Badge key={method} variant="outline" className="text-xs h-5 px-1.5">
                                      {method || 'Unknown'}
                                    </Badge>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(invoice.createdAt, 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewInvoice(invoice)}
                                className="h-8"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {invoice.status !== 'voided' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleVoidInvoice(invoice)}
                                  className="h-8 text-destructive hover:text-destructive"
                                  title="Void Invoice"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Invoice Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedInvoice?.invoiceNumber ? `Invoice #${selectedInvoice.invoiceNumber}` : `Invoice ${selectedInvoice?.id.slice(0, 8)}`}
            </DialogTitle>
            <DialogDescription>
              Complete invoice details and payment history
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="py-12 text-center">
              <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
              <p className="text-muted-foreground">Loading invoice details...</p>
            </div>
          ) : selectedInvoice ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedInvoice.status)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Invoice Number</p>
                    <p className="font-medium">{selectedInvoice.invoiceNumber || selectedInvoice.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Unit</p>
                    <p className="font-medium">{selectedInvoice.unitName || 'Unknown'} {selectedInvoice.unitNumber && `(${selectedInvoice.unitNumber})`}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Event</p>
                    <p className="font-medium">
                      {selectedInvoice.eventName || 'Unknown'}
                      {selectedInvoice.eventDate && ` - ${format(selectedInvoice.eventDate, 'MMM dd, yyyy')}`}
                    </p>
                  </div>
                  {selectedInvoice.ownerName && (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Owner</p>
                        <p className="font-medium">{selectedInvoice.ownerName}</p>
                      </div>
                      {selectedInvoice.ownerEmail && (
                        <div>
                          <p className="text-sm text-muted-foreground">Owner Email</p>
                          <p className="font-medium">{selectedInvoice.ownerEmail}</p>
                        </div>
                      )}
                    </>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">{format(selectedInvoice.createdAt, 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Last Updated</p>
                    <p className="font-medium">{format(selectedInvoice.updatedAt, 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Financial Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="text-2xl font-bold">{formatCurrency(selectedInvoice.totalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Amount Paid</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedInvoice.amountPaid)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency(selectedInvoice.outstandingBalance)}</p>
                  </div>
                  {selectedInvoice.subtotal && (
                    <div>
                      <p className="text-sm text-muted-foreground">Subtotal (before VAT)</p>
                      <p className="text-xl font-semibold">{formatCurrency(selectedInvoice.subtotal)}</p>
                    </div>
                  )}
                  {selectedInvoice.vatAmount && (
                    <div>
                      <p className="text-sm text-muted-foreground">VAT Amount ({selectedInvoice.vatRate ? (selectedInvoice.vatRate * 100).toFixed(0) : '20'}%)</p>
                      <p className="text-xl font-semibold">{formatCurrency(selectedInvoice.vatAmount)}</p>
                    </div>
                  )}
                  {selectedInvoice.transactionFee && (
                    <div>
                      <p className="text-sm text-muted-foreground">Transaction Fee</p>
                      <p className="text-xl font-semibold">{formatCurrency(selectedInvoice.transactionFee)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Accounting Totals */}
              {selectedInvoice.accountingTotals && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Accounting Totals</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Lodge Transfer</p>
                      <p className="text-xl font-semibold">{formatCurrency(selectedInvoice.accountingTotals.totalLodgeTransfer)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Platform Profit</p>
                      <p className="text-xl font-semibold">{formatCurrency(selectedInvoice.accountingTotals.totalPlatformProfit)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment History */}
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Payment History ({selectedInvoice.paymentHistory.length})
                  {selectedInvoice.paymentHistory.length > 0 && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      Total: {formatCurrency(selectedInvoice.paymentHistory.reduce((sum, p) => sum + p.amount, 0))}
                    </span>
                  )}
                </h3>
                {selectedInvoice.paymentHistory.length === 0 ? (
                  <p className="text-muted-foreground">No payments recorded</p>
                ) : (
                  <div className="space-y-3">
                    {selectedInvoice.paymentHistory.map((payment, index) => {
                      const isSumUp = payment.method === 'sumup';
                      const isSlickPay = payment.method === 'slickpay';
                      const isStripe = payment.method === 'stripe';
                      
                      return (
                        <div 
                          key={index} 
                          className={`border rounded-lg p-4 ${
                            isSumUp ? 'bg-blue-50 border-blue-200' : 
                            isSlickPay ? 'bg-cyan-50 border-cyan-200' :
                            isStripe ? 'bg-purple-50 border-purple-200' : 
                            'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {isSumUp ? (
                                <Smartphone className="h-5 w-5 text-blue-600" />
                              ) : isSlickPay ? (
                                <Wallet className="h-5 w-5 text-cyan-700" />
                              ) : isStripe ? (
                                <CreditCard className="h-5 w-5 text-purple-600" />
                              ) : (
                                <Building2 className="h-5 w-5 text-gray-600" />
                              )}
                              <div>
                                <span className="font-semibold text-lg">{formatCurrency(payment.amount)}</span>
                                {payment.method && getPaymentMethodBadge(payment.method)}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-muted-foreground block">
                                {format(payment.date, 'MMM dd, yyyy')}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {format(payment.date, 'HH:mm:ss')}
                              </span>
                            </div>
                          </div>
                          
                          {/* SumUp Specific Details */}
                          {isSumUp && (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Smartphone className="h-4 w-4 text-blue-600" />
                                <span className="font-semibold text-sm text-blue-900">SumUp Payment Details</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {payment.sumupCheckoutId && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Checkout ID:</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{payment.sumupCheckoutId}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(payment.sumupCheckoutId!, 'Checkout ID')}
                                        title="Copy Checkout ID"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => window.open(getSumUpDashboardUrl(payment.sumupCheckoutId!), '_blank')}
                                        title="Open in SumUp Dashboard"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {payment.sumupTransactionCode && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Transaction Code:</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{payment.sumupTransactionCode}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(payment.sumupTransactionCode!, 'Transaction Code')}
                                        title="Copy Transaction Code"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {payment.transactionId && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Transaction ID:</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{payment.transactionId}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(payment.transactionId!, 'Transaction ID')}
                                        title="Copy Transaction ID"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {payment.processingFee && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Processing Fee:</span>
                                    <span className="font-semibold">{formatCurrency(payment.processingFee)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {isSlickPay && (
                            <div className="mt-3 pt-3 border-t border-cyan-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Wallet className="h-4 w-4 text-cyan-700" />
                                <span className="font-semibold text-sm text-cyan-900">SlickPay payment details</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {payment.slickpayInvoiceId && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">SlickPay invoice ID:</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{payment.slickpayInvoiceId}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(payment.slickpayInvoiceId!, 'SlickPay invoice ID')}
                                        title="Copy SlickPay invoice ID"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Stripe Specific Details */}
                          {isStripe && (
                            <div className="mt-3 pt-3 border-t border-purple-200">
                              <div className="flex items-center gap-2 mb-2">
                                <CreditCard className="h-4 w-4 text-purple-600" />
                                <span className="font-semibold text-sm text-purple-900">Stripe Payment Details</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {payment.stripePaymentIntentId && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Payment Intent:</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{payment.stripePaymentIntentId}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(payment.stripePaymentIntentId!, 'Payment Intent ID')}
                                        title="Copy Payment Intent ID"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {payment.stripeChargeId && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Charge ID:</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs">{payment.stripeChargeId}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(payment.stripeChargeId!, 'Charge ID')}
                                        title="Copy Charge ID"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {payment.processingFee && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span className="text-muted-foreground">Processing Fee:</span>
                                    <span className="font-semibold">{formatCurrency(payment.processingFee)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Bank Transfer Details */}
                          {payment.method === 'bank_transfer' && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Building2 className="h-4 w-4 text-gray-600" />
                                <span className="font-semibold text-sm text-gray-900">Bank Transfer Payment</span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {payment.processingFee && (
                                  <div className="flex items-center justify-between bg-white rounded p-2">
                                    <span>Processing Fee:</span>
                                    <span className="font-semibold">{formatCurrency(payment.processingFee)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Accounting Split (if available) */}
                          {payment.accountingSplit && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span className="font-semibold text-sm">Accounting Split</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm bg-white rounded p-2">
                                <div>
                                  <span className="text-muted-foreground">Lodge Transfer:</span>
                                  <span className="font-semibold ml-2">{formatCurrency(payment.accountingSplit.lodgeTransfer)}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Platform Profit:</span>
                                  <span className="font-semibold ml-2">{formatCurrency(payment.accountingSplit.platformProfit)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Audit Log */}
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Audit Log ({auditLogs.length})
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    Payment transactions and system events
                  </span>
                </h3>
                {loadingAuditLogs ? (
                  <div className="py-8 text-center">
                    <div className="mb-4 h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                    <p className="text-muted-foreground text-sm">Loading audit logs...</p>
                  </div>
                ) : auditLogs.length === 0 ? (
                  <p className="text-muted-foreground">No audit logs found</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {auditLogs.map((log) => {
                      const isPaymentProcessed = log.action === 'payment_processed';
                      const isSumUp = log.details?.paymentMethod === 'sumup';
                      const isSlickPay = log.details?.paymentMethod === 'slickpay';
                      const isStripe = log.details?.paymentMethod === 'stripe';
                      
                      return (
                        <div 
                          key={log.id} 
                          className={`border rounded-lg p-3 text-sm ${
                            isPaymentProcessed && isSumUp ? 'bg-blue-50 border-blue-200' :
                            isPaymentProcessed && isSlickPay ? 'bg-cyan-50 border-cyan-200' :
                            isPaymentProcessed && isStripe ? 'bg-purple-50 border-purple-200' :
                            'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {isPaymentProcessed ? (
                                  <CheckCircle2 className={`h-4 w-4 ${isSumUp ? 'text-blue-600' : isSlickPay ? 'text-cyan-700' : isStripe ? 'text-purple-600' : 'text-green-600'}`} />
                                ) : (
                                  <AlertCircle className="h-4 w-4 text-gray-600" />
                                )}
                                <span className="font-semibold">{log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                {isPaymentProcessed && log.details?.paymentMethod && (
                                  getPaymentMethodBadge(log.details.paymentMethod)
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mb-2">
                                {log.userName} • {format(log.timestamp, 'MMM dd, yyyy HH:mm:ss')}
                              </div>
                              
                              {/* Payment Details */}
                              {isPaymentProcessed && (
                                <div className="mt-2 space-y-1 text-xs">
                                  {log.details?.amount && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-muted-foreground">Amount:</span>
                                      <span className="font-semibold">{formatCurrency(log.details.amount)}</span>
                                    </div>
                                  )}
                                  {log.details?.processingFee && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-muted-foreground">Processing Fee:</span>
                                      <span className="font-semibold">{formatCurrency(log.details.processingFee)}</span>
                                    </div>
                                  )}
                                  {isSumUp && (
                                    <>
                                      {log.details?.checkoutId && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-muted-foreground">Checkout ID:</span>
                                          <div className="flex items-center gap-1">
                                            <span className="font-mono text-xs">{log.details.checkoutId}</span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-5 w-5 p-0"
                                              onClick={() => copyToClipboard(log.details.checkoutId, 'Checkout ID')}
                                              title="Copy"
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                      {log.details?.transactionCode && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-muted-foreground">Transaction Code:</span>
                                          <div className="flex items-center gap-1">
                                            <span className="font-mono text-xs">{log.details.transactionCode}</span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-5 w-5 p-0"
                                              onClick={() => copyToClipboard(log.details.transactionCode, 'Transaction Code')}
                                              title="Copy"
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {isSlickPay && log.details?.slickpayInvoiceId && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-muted-foreground">SlickPay invoice ID:</span>
                                      <div className="flex items-center gap-1">
                                        <span className="font-mono text-xs">{String(log.details.slickpayInvoiceId)}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 w-5 p-0"
                                          onClick={() =>
                                            copyToClipboard(String(log.details.slickpayInvoiceId), 'SlickPay invoice ID')
                                          }
                                          title="Copy"
                                        >
                                          <Copy className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  {isStripe && (
                                    <>
                                      {log.details?.paymentIntentId && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-muted-foreground">Payment Intent:</span>
                                          <div className="flex items-center gap-1">
                                            <span className="font-mono text-xs">{log.details.paymentIntentId}</span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-5 w-5 p-0"
                                              onClick={() => copyToClipboard(log.details.paymentIntentId, 'Payment Intent ID')}
                                              title="Copy"
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {log.details?.newStatus && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-muted-foreground">Invoice Status:</span>
                                      <span>{getStatusBadge(log.details.newStatus)}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Other log details */}
                              {!isPaymentProcessed && Object.keys(log.details).length > 0 && (
                                <div className="mt-2 text-xs">
                                  <pre className="bg-white rounded p-2 overflow-x-auto text-xs">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(log.timestamp, 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* RSVP Details */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Associated RSVPs ({selectedInvoice.rsvps.length})</h3>
                {selectedInvoice.rsvpDetails && selectedInvoice.rsvpDetails.length > 0 ? (
                  <div className="space-y-2">
                    {selectedInvoice.rsvpDetails.map((rsvp) => (
                      <div key={rsvp.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{rsvp.name}</p>
                            <p className="text-sm text-muted-foreground">{rsvp.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {rsvp.paid ? (
                              <Badge variant="default" className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Paid
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="flex items-center gap-1">
                                <XCircle className="h-3 w-3" />
                                Unpaid
                              </Badge>
                            )}
                            {rsvp.paymentStatus && (
                              <Badge variant="outline">{rsvp.paymentStatus}</Badge>
                            )}
                            {rsvp.paymentMethod && getPaymentMethodBadge(rsvp.paymentMethod)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No RSVP details available</p>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Invoice Dialog */}
      <Dialog open={isVoidDialogOpen} onOpenChange={setIsVoidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Invoice</DialogTitle>
            <DialogDescription>
              This action will mark the invoice as voided and cannot be undone. Associated RSVPs will be marked as unpaid.
            </DialogDescription>
          </DialogHeader>
          {invoiceToVoid && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm space-y-1">
                  <p><strong>Invoice:</strong> {invoiceToVoid.invoiceNumber ? `#${invoiceToVoid.invoiceNumber}` : invoiceToVoid.id.slice(0, 8)}</p>
                  <p><strong>Unit:</strong> {invoiceToVoid.unitName || 'Unknown'} {invoiceToVoid.unitNumber && `(${invoiceToVoid.unitNumber})`}</p>
                  <p><strong>Event:</strong> {invoiceToVoid.eventName || 'Unknown'}</p>
                  <p><strong>Status:</strong> {invoiceToVoid.status}</p>
                  <p><strong>Total Amount:</strong> {formatCurrency(invoiceToVoid.totalAmount)}</p>
                  <p><strong>Amount Paid:</strong> {formatCurrency(invoiceToVoid.amountPaid)}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Reason for voiding (optional)</label>
                <Textarea
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="Enter reason for voiding this invoice..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsVoidDialogOpen(false);
                setInvoiceToVoid(null);
                setVoidReason('');
              }}
              disabled={voiding}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmVoidInvoice}
              disabled={voiding}
            >
              {voiding ? 'Voiding...' : 'Void Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

